import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Fingerprint, Loader2, X, Check, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'

import {
  isPasskeySupported,
  beginRegistration,
  completeRegistration,
} from '@services/passkey'
import type { RegistrationCredential } from '@services/passkey'

interface PasskeySetupDialogProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

type Step = 'prompt' | 'device-name' | 'saving'

export default function PasskeySetupDialog({
  open,
  onClose,
  onSuccess,
}: PasskeySetupDialogProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>('prompt')
  const [deviceName, setDeviceName] = useState('')
  const [credential, setCredential] = useState<RegistrationCredential | null>(null)
  const [challenge, setChallenge] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [registering, setRegistering] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (step === 'device-name' && inputRef.current) {
      inputRef.current.focus()
    }
  }, [step])

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep('prompt')
      setDeviceName('')
      setCredential(null)
      setChallenge(null)
      setError(null)
      setRegistering(false)
    }
  }, [open])

  if (!open) return null

  const supported = isPasskeySupported()

  const handleBeginRegistration = async () => {
    setError(null)
    setRegistering(true)
    try {
      const result = await beginRegistration()
      setCredential(result.credential)
      setChallenge(result.challenge)
      setStep('device-name')
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError(t('auth.passkey.setupDialog.cancelled'))
      } else if (err instanceof Error && err.message === 'PASSKEY_CANCELLED') {
        setError(t('auth.passkey.setupDialog.cancelled'))
      } else {
        setError(t('auth.passkey.setupDialog.error'))
      }
    } finally {
      setRegistering(false)
    }
  }

  const handleComplete = async () => {
    if (!credential || !challenge) return
    setError(null)
    setStep('saving')
    try {
      await completeRegistration(credential, challenge, deviceName.trim() || undefined)
      toast.success(t('auth.passkey.setupDialog.success'))
      onSuccess?.()
      onClose()
    } catch {
      setError(t('auth.passkey.setupDialog.error'))
      setStep('device-name')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleComplete()
    }
    if (e.key === 'Escape') {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      role="dialog"
      aria-modal="true"
      aria-label={t('auth.passkey.setupDialog.title')}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
              <Fingerprint size={20} className="text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t('auth.passkey.setupDialog.title')}
            </h3>
          </div>
          <button
            data-testid="passkey-setup-dialog-close-btn"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label={t('common.close')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Not supported warning */}
        {!supported && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2 mb-4">
            <AlertTriangle size={16} className="text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-700">
              {t('auth.passkey.setupDialog.notSupported')}
            </p>
          </div>
        )}

        {/* Step: prompt */}
        {step === 'prompt' && supported && (
          <div>
            <p className="text-sm text-gray-600 mb-6">
              {t('auth.passkey.setupDialog.description')}
            </p>
            <button
              data-testid="passkey-setup-dialog-begin-registration-btn"
              onClick={handleBeginRegistration}
              disabled={registering}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
              aria-label={t('auth.passkey.setupDialog.register')}
            >
              {registering ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Fingerprint size={18} />
              )}
              {t('auth.passkey.setupDialog.register')}
            </button>
          </div>
        )}

        {/* Step: device-name */}
        {step === 'device-name' && (
          <div>
            {error ? (
              <div className="flex items-center gap-2 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle size={16} className="text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-700">
                  {t('auth.passkey.setupDialog.saveError')}
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <Check size={16} className="text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-700">
                  {t('auth.passkey.setupDialog.created')}
                </p>
              </div>
            )}
            <label htmlFor="passkey-device-name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.passkey.setupDialog.deviceNameLabel')}
            </label>
            <p className="text-xs text-gray-500 mb-2">
              {t('auth.passkey.setupDialog.deviceNameHint')}
            </p>
            <input
              data-testid="passkey-setup-dialog-passkey-device-name-input"
              ref={inputRef}
              id="passkey-device-name"
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('auth.passkey.setupDialog.deviceNamePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              aria-label={t('auth.passkey.setupDialog.deviceNameLabel')}
            />
            <div className="mt-4 flex gap-3">
              <button
                data-testid="passkey-setup-dialog-complete-btn-1"
                onClick={handleComplete}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                {t('common.save')}
              </button>
              <button
                data-testid="passkey-setup-dialog-complete-btn-2"
                onClick={() => {
                  setDeviceName('')
                  handleComplete()
                }}
                className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('auth.passkey.setupDialog.skipName')}
              </button>
            </div>
          </div>
        )}

        {/* Step: saving */}
        {step === 'saving' && (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={24} className="animate-spin text-primary-600" />
          </div>
        )}

        {/* Error message (only for prompt step; device-name step shows error inline) */}
        {error && step !== 'device-name' && (
          <p className="mt-3 text-sm text-red-600 text-center">
            {error}
          </p>
        )}
      </div>
    </div>
  )
}
